import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
    LayoutDashboard, Wallet, Receipt, TrendingUp, TrendingDown,
    Info, XCircle, ChevronRight, ChevronDown, Plus, Search,
    MoreVertical, FileText, History, Download, Pencil, Trash2,
    Settings, ListTree, Upload, Map
} from 'lucide-react';
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";

import { fetchAccounts, formatCurrency } from "../apiHelper";
import { useToast } from "../hooks/useToast";
import Button from "../components/ui/Button";
import Card from "../components/ui/Card";
import Input from "../components/ui/Input";
import Dropdown from "../components/ui/Dropdown";

// --- LOGIC PRESERVATION: HELPER FUNCTIONS ---
// const buildAccountTree = (accounts) => {
//     const accountMap = new Map();
//     const topLevelAccounts = [];
//     accounts.forEach(account => {
//         account.children = [];
//         accountMap.set(account.account_id, account);
//     });
//     accounts.forEach(account => {
//         if (account.parent && account.parent.account_id) {
//             const parent = accountMap.get(account.parent.account_id);
//             if (parent) parent.children.push(account);
//         } else {
//             topLevelAccounts.push(account);
//         }
//     });
//     return topLevelAccounts;
// };

const buildAccountTree = (formattedAccounts) => {
    // Since formattedAccounts is now recursive and the API
    // returns nested data, we just return the top level.
    return formattedAccounts;
};

// const formatAccounts = (accounts) => {
//     return accounts.map(account => ({
//         ...account,
//         accountName: account.name,
//         accountType: account.account_type?.label || '-',
//         accountCode: account.account_code,
//         parentAccount: account.parent?.name || '-',
//         owner: account.organization?.organization_name || '-',
//         fullAccount: account,
//     }));
// };

const formatAccounts = (accounts) => {
    return accounts.map(account => ({
        ...account,
        accountName: account.name,
        accountType: account.account_type?.label || (typeof account.account_type === 'string' ? account.account_type : '-'),
        accountCode: account.account_code,
        parentAccount: account.parent?.name || '-',
        owner: account.organization?.organization_name || account.organization_name || '-',
        fullAccount: account,
        // Recursively format children if they exist
        children: account.children ? formatAccounts(account.children) : []
    }));
};

// --- MODERN HIERARCHICAL TABLE COMPONENT ---
const HierarchicalTable = ({ data, columns, onAction, rowActions, loading }) => {
    const [expandedRows, setExpandedRows] = useState({});
    const [searchTerm, setSearchTerm] = useState("");

    const toggleRow = (id) => {
        setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
    };

    // Flatten logic for searching while preserving structure
    const getFlattenedRows = (nodes, depth = 0) => {
        let rows = [];
        nodes.forEach(node => {
            const isExpanded = expandedRows[node.account_id];
            rows.push({ ...node, depth });
            if (isExpanded && node.children && node.children.length > 0) {
                rows = [...rows, ...getFlattenedRows(node.children, depth + 1)];
            }
        });
        return rows;
    };

    const visibleRows = useMemo(() => getFlattenedRows(data), [data, expandedRows]);

    const filteredRows = useMemo(() => {
        if (!searchTerm) return visibleRows;
        // When searching, we show all matches regardless of expansion state for ease of use
        const allPossible = (nodes, depth = 0) => {
            let res = [];
            nodes.forEach(n => {
                res.push({ ...n, depth });
                if (n.children) res = [...res, ...allPossible(n.children, depth + 1)];
            });
            return res;
        };
        return allPossible(data).filter(row =>
            row.accountName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            row.accountCode.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [data, visibleRows, searchTerm]);

    return (
        <div className="w-full bg-white rounded-xl shadow-sm border border-zinc-200 overflow-hidden flex flex-col">
            {/* Table Header with Search */}
            <div className="p-4 border-b border-zinc-100 flex flex-col sm:flex-row justify-between items-center gap-4 bg-white">
                <div className="relative w-full sm:w-96">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={18} />
                    <input
                        type="text"
                        placeholder="Search accounts or codes..."
                        className="w-full pl-10 pr-4 py-2 bg-zinc-50 border border-zinc-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <div className="flex items-center gap-2 text-xs text-zinc-500 font-medium bg-zinc-50 px-3 py-1.5 rounded-full border border-zinc-100">
                    <ListTree size={14} />
                    <span>{filteredRows.length} Accounts Found</span>
                </div>
            </div>

            {/* Scrollable Table Area */}
            <div className="overflow-x-auto max-h-[600px] overflow-y-auto relative">
                <table className="w-full text-left border-collapse">
                    <thead className="sticky top-0 z-10">
                        <tr className="bg-zinc-50/90 backdrop-blur-md border-b border-zinc-200">
                            {columns.map(col => (
                                <th key={col.key} className="px-6 py-4 text-[11px] font-bold text-zinc-500 uppercase tracking-wider">
                                    {col.label}
                                </th>
                            ))}
                            <th className="px-6 py-4 text-[11px] font-bold text-zinc-500 uppercase tracking-wider text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-100">
                        {filteredRows.length === 0 ? (
                            <tr>
                                <td colSpan={columns.length + 1} className="px-6 py-12 text-center text-zinc-400 italic">
                                    No accounts found matching your criteria.
                                </td>
                            </tr>
                        ) : (
                            filteredRows.map((row) => (
                                <tr key={row.account_id} className="group hover:bg-zinc-50/50 transition-colors">
                                    {columns.map((col, idx) => (
                                        <td key={col.key} className="px-6 py-4 text-sm text-zinc-600">
                                            <div className="flex items-center" style={{ paddingLeft: idx === 0 ? `${row.depth * 1.5}rem` : '0' }}>
                                                {idx === 0 && (
                                                    <div className="w-6 flex-shrink-0">
                                                        {row.children && row.children.length > 0 ? (
                                                            <button
                                                                onClick={() => toggleRow(row.account_id)}
                                                                className="p-1 hover:bg-zinc-200 rounded text-zinc-400 group-hover:text-zinc-600 transition-colors"
                                                            >
                                                                {expandedRows[row.account_id] ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                                            </button>
                                                        ) : (
                                                            <div className="w-3 h-3 rounded-full border border-zinc-200 ml-1.5 opacity-30" />
                                                        )}
                                                    </div>
                                                )}
                                                <span className={idx === 0 ? "font-medium text-zinc-900" : ""}>
                                                    {col.render ? col.render(row[col.key], row) : row[col.key]}
                                                </span>
                                            </div>
                                        </td>
                                    ))}
                                    <td className="px-6 py-4 text-right">
                                        <div className="flex justify-end">
                                            <Dropdown
                                                trigger={
                                                    <button className="p-2 hover:bg-zinc-100 rounded-lg text-zinc-400 transition-colors">
                                                        <MoreVertical size={18} />
                                                    </button>
                                                }
                                            >
                                                <div className="py-1 min-w-[160px]">
                                                    {rowActions.map(action => (
                                                        <button
                                                            key={action.key}
                                                            onClick={() => onAction(action.key, row)}
                                                            className="flex items-center gap-3 px-4 py-2.5 text-sm text-zinc-600 hover:bg-zinc-50 w-full text-left transition-colors"
                                                        >
                                                            {action.key === 'ledger' && <FileText size={16} />}
                                                            {action.key === 'view' && <History size={16} />}
                                                            {action.key === 'export' && <Download size={16} />}
                                                            {action.label}
                                                        </button>
                                                    ))}
                                                    <div className="border-t border-zinc-100 my-1"></div>
                                                    <button
                                                        onClick={() => onAction('edit', row)}
                                                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-indigo-600 hover:bg-indigo-50 w-full text-left transition-colors"
                                                    >
                                                        <Pencil size={16} /> Edit Account
                                                    </button>
                                                </div>
                                            </Dropdown>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

// --- MAIN ACCOUNTING COMPONENT ---
const NewAccounting = () => {
    const navigate = useNavigate();
    const { showToast } = useToast();

    const [user, setUser] = useState(null);
    const [tableData, setTableData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [summaryData, setSummaryData] = useState({
        totalBalance: 0,
        totalAccounts: 0,
        assetAccounts: 0,
        liabilityAccounts: 0,
    });
    const [pcSymbol, setPcSymbol] = useState('₦');

    const columns = [
        { key: "accountName", label: "Account Name" },
        { key: "accountType", label: "Type" },
        { key: "accountCode", label: "Code" },
        {
            key: "balance",
            label: "Current Balance",
            render: (_, row) => {
                const currencySymbol = row.fullAccount?.balance_snapshots?.[0]?.currency?.setcnote || pcSymbol;
                const balance = row.fullAccount?.current_balance;
                if (balance === undefined || balance === null) return <span className="text-zinc-300">-</span>;
                return (
                    <span className={`font-mono ${parseFloat(balance) < 0 ? 'text-red-500' : 'text-zinc-700'}`}>
                        {currencySymbol}{parseFloat(balance).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </span>
                );
            }
        },
        { key: "owner", label: "Organization" },
    ];

    useEffect(() => {
        const lcUser = localStorage.getItem("user");
        if (!lcUser) {
            showToast("Please log in to view accounting data.", "info");
            navigate('/login');
        } else {
            setUser(JSON.parse(lcUser));
        }
    }, [showToast, navigate]);

    const loadAccounts = useCallback(async () => {
        if (!user) return;
        setLoading(true);
        try {
            const fetchedAccounts = await fetchAccounts({ ...user, offset: 0, limit: 1000 });
            // console.log(fetchedAccounts) // the data in accounts.json
            const formattedAccounts = formatAccounts(fetchedAccounts);
            const hierarchicalData = buildAccountTree(formattedAccounts);
            setTableData(hierarchicalData);

            let totalBalanceSum = 0;
            let assetCount = 0;
            let liabilityCount = 0;
            let totalCount = 0;
            let primarySymbol = '₦';

            // fetchedAccounts.forEach(account => {
            //     const bal = parseFloat(account.current_balance || 0);
            //     const type = account.account_type?.label?.toLowerCase() || '';
            //     if (type.includes('asset')) {
            //         totalBalanceSum += bal;
            //         assetCount++;
            //     } else if (type.includes('liability')) {
            //         totalBalanceSum -= bal;
            //         liabilityCount++;
            //     }
            //     if (account.balance_snapshots?.[0]?.currency?.setcnote) {
            //         primarySymbol = account.balance_snapshots[0].currency.setcnote;
            //     }
            // });

            // setPcSymbol(primarySymbol);
            // setSummaryData({
            //     totalBalance: totalBalanceSum,
            //     totalAccounts: fetchedAccounts.length,
            //     assetAccounts: assetCount,
            //     liabilityAccounts: liabilityCount,
            // });

            const processAccount = (account) => {
                totalCount++;
                const bal = parseFloat(account.current_balance || 0);
                const type = (account.account_type?.label || account.account_type || '').toLowerCase();

                if (type.includes('asset') || type.includes('bank') || type.includes('receivable')) {
                    totalBalanceSum += bal;
                    assetCount++;
                } else if (type.includes('liability') || type.includes('payable')) {
                    totalBalanceSum -= bal;
                    liabilityCount++;
                }

                if (account.balance_snapshots?.[0]?.currency?.setcnote) {
                    primarySymbol = account.balance_snapshots[0].currency.setcnote;
                }

                // Process children recursively
                if (account.children && account.children.length > 0) {
                    account.children.forEach(processAccount);
                }
            };

            fetchedAccounts.forEach(processAccount);

            setPcSymbol(primarySymbol);
            setSummaryData({
                totalBalance: totalBalanceSum,
                totalAccounts: totalCount,
                assetAccounts: assetCount,
                liabilityAccounts: liabilityCount,
            });

        } catch (err) {
            setError(err.response?.data?.detail || "Failed to load accounts");
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => { if (user) loadAccounts(); }, [user, loadAccounts]);

    const handleRowAction = (key, row) => {
        if (key === 'edit') {
            navigate('/accounting/edit', { state: row.fullAccount });
        } else if (key === "view") {
            navigate('/accounting/details', { state: { accountId: row.fullAccount.account_id, accountName: row.accountName, user } });
        } else if (key === "ledger") {
            navigate('/reports/account-ledger-report', { state: { account_id: row.fullAccount.account_id } });
        } else {
            showToast(`Action ${key} triggered for ${row.accountName}`, 'info');
        }
    };

    if (loading) return (
        <div className='flex flex-col items-center justify-center min-h-[60vh] gap-4'>
            <div className="w-10 h-10 border-4 border-indigo-500/20 border-t-indigo-600 rounded-full animate-spin" />
            <p className="text-zinc-500 font-medium">Synchronizing accounts...</p>
        </div>
    );

    return (
        <div className="flex flex-col min-h-screen bg-[#F9FAFB]">
            {/* Sophisticated Header */}
            <header className="px-6 py-8 border-b border-zinc-200 bg-white">
                <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-zinc-900 flex items-center gap-2">
                            Chart of Accounts
                        </h1>
                        <p className="text-sm text-zinc-500 mt-1">Manage and monitor your financial structure and balances.</p>
                    </div>
                    {/* <div className="flex items-center gap-3">
                        <Button variant="secondary" className="flex items-center justify-center bg-white border-zinc-200 text-zinc-700 shadow-sm" onClick={() => navigate('/accounting/mapping-manager')}>
                            <Settings size={18} className="mr-2" /> Mapping
                        </Button>
                        <Button variant="primary" className="flex items-center justify-center bg-indigo-600 hover:bg-indigo-700 shadow-sm" onClick={() => navigate('/accounting/new-journal-entry')}>
                            <Pencil size={18} className="mr-2" /> Journal Entry
                        </Button>
                    </div> */}
                    <div className="flex items-center gap-3">
                        {/* Settings / Configurations Dropdown */}
                        <Dropdown
                            trigger={
                                <Button
                                    variant="secondary"
                                    className="flex items-center justify-center bg-white border-zinc-200 text-zinc-700 shadow-sm"
                                >
                                    <Settings size={18} className="mr-2 text-zinc-500" />
                                    Configurations
                                    <ChevronDown size={14} className="ml-2 text-zinc-400" />
                                </Button>
                            }
                        >
                            <div className="py-1 min-w-[200px]">
                                {/* Mapping Option */}
                                <button
                                    onClick={() => navigate('/accounting/mapping-manager')}
                                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-zinc-700 hover:bg-zinc-50 w-full text-left transition-colors font-medium"
                                >
                                    <Map size={16} className="text-zinc-500" />
                                    Account Mapping
                                </button>

                                {/* Upload Accounts Option */}
                                <button
                                    onClick={() =>
                                        navigate('/import-data', {
                                            state: {
                                                target: 'bankaccounts'
                                            }
                                        })
                                    }
                                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-zinc-700 hover:bg-zinc-50 w-full text-left transition-colors font-medium"
                                >
                                    <Upload size={16} className="text-zinc-500" />
                                    Upload Banks
                                </button>
                                <button 
                                    onClick={() => navigate('/accounting/store-account-mapping')}
                                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-zinc-700 hover:bg-zinc-50 w-full text-left transition-colors font-medium"
                                >
                                    <Plus size={16} className="text-zinc-500" />
                                    Assign Accounts
                                </button>
                            </div>
                        </Dropdown>

                        {/* Journal Entry Button */}
                        <Button
                            variant="primary"
                            className="flex items-center justify-center bg-indigo-600 hover:bg-indigo-700 shadow-sm"
                            onClick={() => navigate('/accounting/new-journal-entry')}
                        >
                            <Pencil size={18} className="mr-2" /> Journal Entry
                        </Button>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto w-full p-6 space-y-6">
                {/* Modern Summary Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <StatCard label="Net Asset Value" value={formatCurrency(summaryData.totalBalance, 2, pcSymbol)} icon={<Receipt className="text-indigo-600" />} />
                    <StatCard label="Total Accounts" value={summaryData.totalAccounts} icon={<Wallet className="text-zinc-400" />} />
                    <StatCard label="Asset Accounts" value={summaryData.assetAccounts} icon={<TrendingUp className="text-emerald-500" />} />
                    <StatCard label="Liabilities" value={summaryData.liabilityAccounts} icon={<TrendingDown className="text-rose-500" />} />
                </div>

                {/* The Table */}
                <div className="pb-10">
                    <HierarchicalTable
                        data={tableData}
                        columns={columns}
                        onAction={handleRowAction}
                        rowActions={[
                            { label: "View Ledger", key: "ledger" },
                            // { label: "Transaction History", key: "view" },
                            { label: "Export Data", key: "export" },
                        ]}
                    />
                </div>
            </main>
            <button
                onClick={() => navigate('/accounting/new-account')}
                className="fixed bottom-8 right-8 w-14 h-14 bg-indigo-600 text-white rounded-full shadow-2xl hover:bg-indigo-700 flex items-center justify-center transition-all hover:scale-110 active:scale-95 z-50 group"
            >
                <Plus size={28} className="group-hover:rotate-90 transition-transform duration-300" />
            </button>
        </div>
    );
};

const StatCard = ({ label, value, icon }) => (
    <Card className="p-6 border-zinc-200/60 shadow-sm hover:shadow-md transition-shadow">
        <div className="flex items-start justify-between">
            <div>
                <p className="text-xs font-bold text-zinc-500 uppercase tracking-tight mb-1">{label}</p>
                <h3 className="text-2xl font-bold text-zinc-900 tracking-tight">{value}</h3>
            </div>
            <div className="p-3 bg-zinc-50 rounded-xl border border-zinc-100">
                {React.cloneElement(icon, { size: 24 })}
            </div>
        </div>
    </Card>
);

export default NewAccounting;